import { useState, useEffect, useRef, useCallback } from 'react';
import Video from 'twilio-video';
import TelehealthService from '../services/telehealthService';

export const useTwilioRoom = (sessionId, userId) => {
  const [room, setRoom] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [error, setError] = useState(null);
  const callStartTime = useRef(Date.now());
  const activeConnectionKey = `telehealth-active-${sessionId}-${userId}`;

  const connectToRoom = useCallback(async (localTracks = []) => {
    if (!sessionId || !userId) {
      setError('Missing session ID or user ID');
      return null;
    }

    // Check if there's already an active connection for this session
    const existingConnection = sessionStorage.getItem(activeConnectionKey);
    if (existingConnection) {
      setError('You are already connected to this session in another tab or window. Please close other instances and try again.');
      return null;
    }

    setIsConnecting(true);
    setError(null);

    try {
      const token = await TelehealthService.getTwilioVideoToken(sessionId, userId);
      
      const videoRoom = await Video.connect(token, {
        name: sessionId,
        tracks: localTracks,
      });

      setRoom(videoRoom);
      setIsConnected(true);
      
      // Mark this session as active
      sessionStorage.setItem(activeConnectionKey, 'true');
      
      console.log('Connected to room:', sessionId);
      return videoRoom;
      
    } catch (err) {
      console.error('Error joining room:', err);
      setError(`Failed to join video call: ${err.message}`);
      setIsConnecting(false);
      return null;
    }
  }, [sessionId, userId, activeConnectionKey]);

  const disconnectFromRoom = useCallback(() => {
    if (room) {
      console.log('Disconnecting from room');
      room.disconnect();
    }
  }, [room]);

  // Handle room disconnection events
  useEffect(() => {
    if (!room) return;

    const handleDisconnected = (room, error) => {
      console.log('Room disconnected:', error);
      setIsConnected(false);
      setIsConnecting(false);
      setRoom(null);
      
      // Remove the active connection marker
      sessionStorage.removeItem(activeConnectionKey);
      
      // Clean up local tracks
      if (room.localParticipant) {
        room.localParticipant.tracks.forEach(publication => {
          if (publication.track && typeof publication.track.stop === 'function') {
            publication.track.stop();
          }
        });
      }
      
      const callDuration = Date.now() - callStartTime.current;
      console.log('Call ended:', { sessionId, timestamp: Date.now(), duration: callDuration });
    };

    const handleReconnecting = (error) => {
      console.log('Reconnecting because', error);
      setIsReconnecting(true);
    };

    const handleReconnected = () => {
      console.log('Reconnected!');
      setIsReconnecting(false);
    };

    room.once('disconnected', handleDisconnected);
    room.on('reconnecting', handleReconnecting);
    room.on('reconnected', handleReconnected);

    return () => {
      room.off('disconnected', handleDisconnected);
      room.off('reconnecting', handleReconnecting);
      room.off('reconnected', handleReconnected);
    };
  }, [room, sessionId, activeConnectionKey]);

  // Handle page unload and component cleanup
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (room) {
        console.log('Page unloading, disconnecting from room');
        room.disconnect();
      }
    };

    const handlePageHide = () => {
      if (room) {
        console.log('Page hiding, disconnecting from room');
        room.disconnect();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide); // iOS Safari

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      
      // Cleanup on component unmount
      if (room) {
        console.log('Component unmounting, disconnecting from room');
        room.disconnect();
      }
      // Clean up sessionStorage
      sessionStorage.removeItem(activeConnectionKey);
    };
  }, [room, activeConnectionKey]);

  return {
    room,
    isConnected,
    isConnecting,
    isReconnecting,
    error,
    connectToRoom,
    disconnectFromRoom,
    callDuration: () => Date.now() - callStartTime.current
  };
}; 